import { ComponentProps } from 'react';
import { CampaignType_Enum as CampaignType } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { Trans, useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { InlineNotification, SpinningLoader, Text, TextLink } from '@frontend/design-system';
import { useBulkNavigator } from '../../hooks/use-bulk-navigator';
import { useEmailAddresses } from './hooks/use-email-addresses';

type EmailAddressesProps = ComponentProps<typeof Text> & {
  locationIds: string[];
};

// TODO: Improve multilocation support by showing warning if locations aren't properly set up
export const EmailAddresses = ({ locationIds, ...rest }: EmailAddressesProps) => {
  const { t } = useTranslation('bulk-messaging');
  const navigateTo = useBulkNavigator(CampaignType.EMAIL);
  const { fromEmail, fromName, isLoading, replyToEmail, replyToName, warning } = useEmailAddresses(locationIds);

  return (
    <Text as='div' {...rest}>
      {locationIds.length > 1 ? (
        <Trans t={t}>
          Manage reply-to and from email addresses in
          <TextLink onClick={navigateTo.settings} css={{ marginLeft: theme.spacing(0.5) }}>
            Email Settings
          </TextLink>
        </Trans>
      ) : (
        <>
          <p css={{ marginBottom: theme.spacing(0.5) }}>
            <Trans t={t}>
              Send From:
              <EmailAddress email={fromEmail} isLoading={isLoading} name={fromName} />
            </Trans>
          </p>
          <p css={{ marginBottom: theme.spacing(1) }}>
            <Trans t={t}>
              Replies Go To:
              <EmailAddress email={replyToEmail} isLoading={isLoading} name={replyToName} />
            </Trans>
          </p>
        </>
      )}
      {warning && <InlineNotification css={{ marginTop: theme.spacing(2) }} message={warning} status='warn' />}
    </Text>
  );
};

type EmailAddressProps = {
  email: string;
  isLoading: boolean;
  name: string;
};

const EmailAddress = ({ email, isLoading, name }: EmailAddressProps) => {
  const navigateTo = useBulkNavigator(CampaignType.EMAIL);

  if (isLoading) {
    return (
      <SpinningLoader
        size='xs'
        css={{
          position: 'absolute',
          marginLeft: theme.spacing(1),
        }}
      />
    );
  }

  // This state should only happen if there are no selected locations
  if (!email && !name) {
    return <strong css={{ marginLeft: theme.spacing(1) }}>—</strong>;
  }

  // There will always be an email and a name if there's at least one location selected
  return (
    <TextLink onClick={navigateTo.settings} css={{ marginLeft: theme.spacing(1) }}>
      <strong css={{ marginRight: theme.spacing(0.5) }}>{name}</strong>&lt;{email}&gt;
    </TextLink>
  );
};
